import {
  CannabinoidsStudiesQuery,
  ConditionsStudiesQuery,
  LocationsStudiesQuery,
  OrganSystemsStudiesQuery,
  Research_Cannabinoids_Studies_Bool_Exp,
  Research_Medical_Conditions_Studies_Bool_Exp,
  Research_Studies_Locations_Bool_Exp,
  Research_Organ_Systems_Studies_Bool_Exp,
  Research_Studies_Bool_Exp,
  String_Comparison_Exp,
  StudiesByYearQuery,
  StudyTypesQuery,
} from 'graphql/research/types-and-hooks';
import type {
  ConditionsByScrappingIdsQuery,
  ConditionsByScrappingIdsQueryVariables,
} from 'graphql/wp/types-and-hooks';
import type { QueryError, QueryProps } from 'lib/api';
import type { LanguageCodeFilterEnum } from './settings';
import type { ConditionTypesResult } from './conditions';
import type { GetStudiesAggregate, GetResultsAggregateCount } from './studies';

import {
  refetchCannabinoidsStudiesQuery as fetchCannabinoidsStudiesQuery,
  refetchConditionsStudiesQuery as fetchConditionsStudiesQuery,
  refetchLocationsStudiesQuery as fetchLocationsStudiesQuery,
  refetchOrganSystemsStudiesQuery as fetchOrganSystemsStudiesQuery,
  refetchStudyTypesQuery as fetchStudyTypesQuery,
  refetchStudiesByYearQuery as fetchStudiesByYearQuery,
} from 'graphql/research/types-and-hooks';
import { refetchConditionsByScrappingIdsQuery as fetchConditionsByScrappingIdsQuery } from 'graphql/wp/types-and-hooks';
import { getConditionTypes } from './conditions';
import { getResultsAggregateCount, getStudiesAggregate } from './studies';
import { queryResearch } from 'lib/apollo';

export enum DASHBOARD_FILTERS {
  CANNABINOIDS = 'cannabinoids',
  CONDITIONS = 'conditions',
  LOCATIONS = 'locations',
  ORGAN_SYSTEMS = 'organ_systems',
  RESULTS = 'result',
  TYPES = 'typeofstudy',
}

export type DashboardFilters = Record<DASHBOARD_FILTERS, string[]>;

export type DashboardFilterKeys = keyof DashboardFilters;

export type GetDashboardFilters = {
  [DASHBOARD_FILTERS.CANNABINOIDS]: CannabinoidsStudiesQuery['nodes'];
  [DASHBOARD_FILTERS.CONDITIONS]: ConditionsStudiesQuery;
  [DASHBOARD_FILTERS.LOCATIONS]: LocationsStudiesQuery['nodes'];
  [DASHBOARD_FILTERS.ORGAN_SYSTEMS]: OrganSystemsStudiesQuery;
  [DASHBOARD_FILTERS.RESULTS]: GetResultsAggregateCount;
  [DASHBOARD_FILTERS.TYPES]: StudyTypesQuery['nodes'];
};

export async function getDashboardFilters({
  client,
}: Pick<QueryProps, 'client'>): Promise<GetDashboardFilters | QueryError> {
  const [
    cannabinoidsData,
    conditionsData,
    locationsData,
    organSystemsData,
    results,
    studyTypesData,
  ] = await Promise.all([
    queryResearch<CannabinoidsStudiesQuery>(
      client,
      fetchCannabinoidsStudiesQuery(),
    ),
    queryResearch<ConditionsStudiesQuery>(
      client,
      fetchConditionsStudiesQuery(),
    ),
    queryResearch<LocationsStudiesQuery>(client, fetchLocationsStudiesQuery()),
    queryResearch<OrganSystemsStudiesQuery>(
      client,
      fetchOrganSystemsStudiesQuery(),
    ),
    getResultsAggregateCount({
      client,
    }),
    queryResearch<StudyTypesQuery>(client, fetchStudyTypesQuery()),
  ]);
  const { data: cannabinoids, error: errorCannabinoids } = cannabinoidsData;
  if (errorCannabinoids) {
    return { error: errorCannabinoids };
  }

  const { data: conditions, error: conditionsError } = conditionsData;
  if (conditionsError) {
    return { error: conditionsError };
  }

  const { data: locations, error: errorLocations } = locationsData;
  if (errorLocations) {
    return { error: errorLocations };
  }

  const { data: systems, error: errorSystems } = organSystemsData;
  if (errorSystems) {
    return { error: errorSystems };
  }

  if ((results as QueryError)?.error) {
    return { error: (results as QueryError)?.error };
  }

  const { data: types, error: errorTypes } = studyTypesData;
  if (errorTypes) {
    return { error: errorTypes };
  }

  return {
    [DASHBOARD_FILTERS.CANNABINOIDS]: cannabinoids?.nodes || [],
    [DASHBOARD_FILTERS.CONDITIONS]: conditions,
    [DASHBOARD_FILTERS.LOCATIONS]: locations?.nodes,
    [DASHBOARD_FILTERS.ORGAN_SYSTEMS]: systems,
    [DASHBOARD_FILTERS.RESULTS]: results as GetResultsAggregateCount,
    [DASHBOARD_FILTERS.TYPES]: types?.nodes || [],
  };
}

export type GetDashboardInitialData = GetDashboardFilters & {
  studies: {
    byYear: StudiesByYearQuery['nodes'];
    paginated: GetStudiesAggregate['nodes'];
    total: GetStudiesAggregate['count'];
  };
  wp: Pick<ConditionTypesResult, 'conditionTypes'> & {
    conditions: ConditionsByScrappingIdsQuery['conditions']['nodes'];
  };
};

export async function getDashboardInitialData({
  client,
  languageCode,
  locale,
}: Pick<QueryProps, 'client'> & {
  languageCode: LanguageCodeFilterEnum;
  locale?: string;
}): Promise<GetDashboardInitialData | QueryError> {
  const [
    conditionTypesData,
    conditions,
    filters,
    studiesByYearData,
    studiesPaginated,
  ] = await Promise.all([
    getConditionTypes(locale),
    client.query<
      ConditionsByScrappingIdsQuery,
      ConditionsByScrappingIdsQueryVariables
    >(fetchConditionsByScrappingIdsQuery({ languageCode })),
    getDashboardFilters({
      client,
    }),
    queryResearch<StudiesByYearQuery>(client, fetchStudiesByYearQuery()),
    getStudiesAggregate({
      client,
    }),
  ]);
  const { conditionTypes } = conditionTypesData;

  const { data: conditionsData, error: conditionsError } = conditions;
  if (conditionsError) {
    return { error: conditionsError };
  }

  if ((filters as QueryError)?.error) {
    return { error: (filters as QueryError)?.error };
  }

  const { data: studiesByYear, error: errorStudiesByYear } = studiesByYearData;
  if (errorStudiesByYear) {
    return { error: errorStudiesByYear };
  }

  if ((studiesPaginated as QueryError)?.error) {
    return { error: (studiesPaginated as QueryError)?.error };
  }

  return {
    ...filters,
    wp: { conditions: conditionsData?.conditions?.nodes || [], conditionTypes },
    studies: {
      byYear: studiesByYear?.nodes || [],
      paginated: (studiesPaginated as GetStudiesAggregate).nodes,
      total: (studiesPaginated as GetStudiesAggregate).count,
    },
  };
}

export type MapConditions = (ConditionsStudiesQuery['nodes'][0] &
  Pick<ConditionsByScrappingIdsQuery['conditions']['nodes'][0], 'uri'>)[];

export function mapConditions({
  conditions,
  wpData,
}: {
  conditions: ConditionsStudiesQuery['nodes'];
  wpData: GetDashboardInitialData['wp']['conditions'];
}): MapConditions {
  return (
    conditions?.map(condition => {
      const wpCondition = wpData?.find(
        ({ scrapping }) => scrapping?.id == condition.id,
      );

      return { ...condition, uri: wpCondition?.uri || null };
    }) || []
  );
}

export type MapOrganSystems = (OrganSystemsStudiesQuery['nodes'][0] &
  Partial<
    Pick<ConditionTypesResult['conditionTypes'][0]['acf'], 'icon' | 'position'>
  >)[];

export function mapOrganSystems({
  systems,
  wpData,
}: {
  systems: OrganSystemsStudiesQuery['nodes'];
  wpData: GetDashboardInitialData['wp']['conditionTypes'];
}): MapOrganSystems {
  return (
    systems.map(system => {
      const wpConditionType = wpData?.find(
        ({ acf: { leafwellScrappingId } }) => leafwellScrappingId == system.id,
      );

      return {
        ...system,
        ...(wpConditionType
          ? {
              icon: wpConditionType?.acf?.icon,
              position: wpConditionType?.acf?.position,
            }
          : {}),
      };
    }) || []
  );
}

export type StudyQueryFilters = Research_Studies_Bool_Exp;

export type StudyQueryFiltersPartial = Partial<
  Pick<
    Research_Studies_Bool_Exp,
    | 'cannabinoids'
    | 'conditions'
    | 'locations'
    | 'organ_systems'
    | 'result'
    | 'typeofstudy'
  >
>;

export function mapQueryFilters(
  data: DashboardFilters,
): Research_Studies_Bool_Exp {
  return Object.keys(data).reduce(
    (filters: StudyQueryFiltersPartial, key: DashboardFilterKeys) => {
      const value = data[key];

      if (!value || !value.length) {
        return filters;
      }

      switch (key) {
        case DASHBOARD_FILTERS.CANNABINOIDS:
          return {
            ...filters,
            [DASHBOARD_FILTERS.CANNABINOIDS]: {
              cannabinoid_id: { _in: value },
            } as Research_Cannabinoids_Studies_Bool_Exp,
          };
        case DASHBOARD_FILTERS.CONDITIONS:
          return {
            ...filters,
            [DASHBOARD_FILTERS.CONDITIONS]: {
              medical_condition_id: { _in: value },
            } as Research_Medical_Conditions_Studies_Bool_Exp,
          };
        case DASHBOARD_FILTERS.LOCATIONS:
          return {
            ...filters,
            [DASHBOARD_FILTERS.LOCATIONS]: {
              location_id: { _in: value },
            } as Research_Studies_Locations_Bool_Exp,
          };
        case DASHBOARD_FILTERS.ORGAN_SYSTEMS:
          return {
            ...filters,
            [DASHBOARD_FILTERS.ORGAN_SYSTEMS]: {
              organ_system_id: { _in: value },
            } as Research_Organ_Systems_Studies_Bool_Exp,
          };
        case DASHBOARD_FILTERS.RESULTS:
          return {
            ...filters,
            [DASHBOARD_FILTERS.RESULTS]: {
              _in: value,
            } as String_Comparison_Exp,
          };
        case DASHBOARD_FILTERS.TYPES:
          return {
            ...filters,
            [DASHBOARD_FILTERS.TYPES]: {
              _in: value,
            } as String_Comparison_Exp,
          };
        default:
          return filters;
      }
    },
    {},
  ) as Research_Studies_Bool_Exp;
}
